import React from 'react';
import { get, isNil } from 'lodash';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import ResponseCardBlockHolder from 'routes/screens/response/screens/view/components/response-card-block-holder';
import { ResponseCardContent } from 'routes/screens/response/screens/view/style';
import { getOrderedPageData } from 'routes/screens/response/utils';
import Icon, { GlyphType } from 'shared/components/Icon';
import {
  useGetPlaceholderValues,
  useGetSurvey,
  useGetSurveyResponseById,
} from 'shared/hooks';
import {
  AccountInfoType,
  CollectorChannelType,
  RangeLabelType,
  SurveyBlockWithQuestionType,
  SurveyQuestionType,
  SurveyResponseType,
} from 'shared/types';
import { getAccountTimeFormat, getChannelName } from 'shared/utils';
import { getBlocks } from './utils';
import { RatingAvatar, ResponseCardContainer, TimelineCard } from '../styles';

type ResponseCardProps = {
  response: SurveyResponseType;
  accountInfo?: AccountInfoType;
};

const getIconGlyph = (channelType: CollectorChannelType): GlyphType => {
  switch (channelType) {
    case 'EMAIL':
      return 'mail';
    case 'EMAIL_EMBED':
      return 'email-embed';
    case 'WEB_APP':
      return 'web';
    case 'WEB_LINK':
      return 'share-link';
  }
  return 'mail';
};

const ResponseCard = (props: ResponseCardProps) => {
  const { t } = useTranslation();
  const { response, accountInfo } = props;

  const {
    data: surveyResponse,
    isFetching: isFetchingSurveyResponse,
    status: surveyResponseStatus,
  } = useGetSurveyResponseById({
    surveyId: response.survey_id || '',
    responseId: response.id || '',
  });

  const {
    data: survey = {},
    isFetching: isFetchingSurvey,
    status: surveyStatus,
    refetch: refetchSurvey,
  } = useGetSurvey({
    surveyId: response.survey_id || '',
    refetchOnMount: 'always',
    versionId: surveyResponse?.survey_version_id || '',
  });

  const {
    data: placeholdersValuesData = {},
    isFetching: isFetchingPlaceholders,
    status: placeholderStatus,
  } = useGetPlaceholderValues(response.survey_id || '');

  const surveyResponseTime = moment(surveyResponse?.created_at)
    .tz(accountInfo?.timezone || moment.tz.guess())
    .format(getAccountTimeFormat(accountInfo));

  const surveyName = get(survey, ['name'], '');

  const blocks: SurveyBlockWithQuestionType[] = getBlocks({
    surveyInfo: survey,
  });

  const page_data = getOrderedPageData(surveyResponse?.page_data, survey);

  const rating = surveyResponse?.[`${survey?.type?.toLowerCase()}_rating`];
  const overAllScore = surveyResponse?.detailed_response_score;

  const { ratingType, ratingTypeId } = React.useMemo<{
    ratingType?: RangeLabelType | 'default';
    ratingTypeId?: string;
  }>(() => {
    const themeQuestion = survey?.question_details?.questions?.find(
      (que: SurveyQuestionType) => !!que.type_info?.pivot_question,
    );
    if (themeQuestion && surveyResponse && survey) {
      const type = themeQuestion?.type_info?.scale_properties?.range?.find(
        (branch) => {
          return rating >= Number(branch.min) && rating <= Number(branch.max);
        },
      );
      const ratingGroupName = `${survey.type?.toLowerCase()}_rating_group`;
      const ratingType: RangeLabelType =
        surveyResponse[ratingGroupName]?.toLowerCase() || type?.label;

      return {
        ratingType,
        ratingTypeId: type?.id || undefined,
      };
    }
    return { ratingType: 'default', ratingTypeId: undefined };
  }, [surveyResponse, survey]);

  React.useEffect(() => {
    !isFetchingSurveyResponse && refetchSurvey();
  }, [isFetchingSurveyResponse]);

  if (
    isFetchingSurveyResponse ||
    isFetchingSurvey ||
    isFetchingPlaceholders ||
    placeholderStatus === 'idle' ||
    surveyResponseStatus === 'idle' ||
    surveyStatus === 'idle'
  )
    return (
      <TimelineCard key={response.id}>
        <div className="timeline">
          <Icon glyph="calendar" width={14} height={14} viewBox="0 0 14 14" />
          <div className="line"></div>
        </div>
        <div className="response-info">
          <div className="time-info">
            <span>{surveyResponseTime}</span>
            <div className="divider" />
            <Icon
              glyph={getIconGlyph(response.channel || 'EMAIL')}
              width={12}
              height={10}
              viewBox="0 0 12 12"
            />
            <span className="channel">
              {response?.channel && getChannelName(response?.channel)
                ? t(getChannelName(response?.channel))
                : `${t('email_cp')}`}
            </span>
            <div className="divider" />
            {surveyName && <span className="survey-name">{surveyName}</span>}
          </div>
          <ResponseCardContainer>
            <RatingAvatar
              ratingType={ratingType || 'default'}
              className="avatar"
            >
              {rating}
            </RatingAvatar>
            <div className="response-loading">
              <Skeleton height={38} />
            </div>
          </ResponseCardContainer>
        </div>
      </TimelineCard>
    );

  return surveyResponse ? (
    <TimelineCard key={surveyResponse?.id}>
      <div className="timeline">
        <Icon glyph="calendar" width={14} height={14} viewBox="0 0 14 14" />
        <div className="line"></div>
      </div>
      <div className="response-info">
        <div className="time-info">
          <span>{surveyResponseTime}</span>
          <div className="divider" />
          <Icon
            glyph={getIconGlyph(surveyResponse?.channel || 'EMAIL')}
            width={12}
            height={10}
            viewBox="0 0 12 12"
          />
          <span className="channel">
            {surveyResponse?.channel && getChannelName(surveyResponse?.channel)
              ? t(getChannelName(surveyResponse?.channel))
              : `${t('email_cp')}`}
          </span>
          <div className="divider" />
          {surveyName && <span className="survey-name">{surveyName}</span>}
        </div>
        <ResponseCardContainer isPages={page_data?.length}>
          {survey.type !== 'NONE' && (
            <RatingAvatar
              ratingType={ratingType || 'default'}
              className="avatar"
            >
              {rating}
            </RatingAvatar>
          )}
          <ResponseCardContent
            className={`content ${
              response.is_invalid ? 'invalid-content' : ''
            }`}
          >
            <>
              <ResponseCardBlockHolder
                compactMode={true}
                survey={survey}
                response={surveyResponse}
                surveyBlocks={blocks}
                ratingTypeId={ratingTypeId}
                accountInfo={accountInfo}
                placeholders={placeholdersValuesData?.placeholders || {}}
              />
              {!isNil(overAllScore) && (
                <div className="overall-score">
                  <label>{t('responses.card.overall_score')}</label>
                  <div className="score-chip">
                    <span className="given-score">
                      {overAllScore?.cumulative_given_score}
                    </span>
                    <span className="max-score">
                      {overAllScore?.cumulative_max_score}
                    </span>
                  </div>
                </div>
              )}
            </>
          </ResponseCardContent>
        </ResponseCardContainer>
      </div>
    </TimelineCard>
  ) : null;
};

export default ResponseCard;
